import { Add, Lock } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { map } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';

import {
  Cutsheet,
  EnumCutsheetType,
  ProducerJobByIdDocument,
  SortFindManyCutsheetInput,
  useCutsheetSearchQuery,
  useProcessorSchedulingCutsheetsPartialUpdateMutation,
} from 'lib/graphql';
import { isBuyerOnly } from 'lib/userHelpers';
import { userState } from 'state';

import { CutsheetCard } from './cutsheet-card';
import { SplitSlotDrawer } from './split-slot/split-slot-drawer';
import {
  asInspectionLevel,
  asSpecies,
  type DrawerCutsheetInfo,
  type DrawerHead,
  type DrawerJob,
} from './split-slot/types';

interface ProducerCutsheetDrawerProps {
  open: boolean;
  onClose: () => void;
  onCreateNew: () => void;
  /**
   * Called after a cutsheet is successfully assigned. Parents use this to
   * trigger view-specific refetches. When omitted the drawer falls back to
   * refetching ProducerJobByIdDocument automatically.
   */
  onCutsheetAssigned?: () => void | Promise<void>;
  job: DrawerJob;
  head: DrawerHead;
  cutsheetInfo: DrawerCutsheetInfo;
}

export function ProducerCutsheetDrawer({
  open,
  onClose,
  onCreateNew,
  onCutsheetAssigned,
  job,
  head,
  cutsheetInfo,
}: ProducerCutsheetDrawerProps) {
  const user = useRecoilValue(userState);
  const [activeTab, setActiveTab] = useState(0);

  const vendorId = job.vendor?._id;
  const species = asSpecies(job.animalSpecies);
  const inspectionLevel = asInspectionLevel(head.inspectionLevel);

  const buyerMode = user ? isBuyerOnly(user) : false;

  const contactUserId = cutsheetInfo.contactUser?._id;
  const isSelfContact = contactUserId === user?._id;
  const showBuyerTab = !isSelfContact && !!contactUserId;
  const producerId = job.requestedBy?._id;
  const showProducerTab = buyerMode && !!producerId;

  const isLocked = cutsheetInfo.locked === true;
  const currentCutsheetId = cutsheetInfo.cutsheet?._id;

  const defaultQueryVars = {
    sort: SortFindManyCutsheetInput.IdDesc,
    page: 1,
    perPage: 100,
    includeDeleted: false,
    species,
    inspectionLevel,
    vendorId,
  };

  // Query 1: House cutsheets (vendor-owned)
  const { data: houseData, loading: houseLoading } = useCutsheetSearchQuery({
    variables: {
      ...defaultQueryVars,
      type: [EnumCutsheetType.Vendor],
    },
    skip: !open || !vendorId,
    fetchPolicy: 'cache-and-network',
  });

  // Query 2: My cutsheets — matches CutsheetSelectionGrid "Your Custom Cutsheets"
  // Producers: userId (direct ownership)
  // Buyers: buyerId (ownership + previously used cutsheets via $or)
  const { data: myData, loading: myLoading } = useCutsheetSearchQuery({
    variables: {
      ...defaultQueryVars,
      type: [EnumCutsheetType.User],
      ...(buyerMode ? { buyerId: user?._id } : { userId: user?._id }),
    },
    skip: !open || !vendorId || !user?._id,
    fetchPolicy: 'cache-and-network',
  });

  // Query 3: Buyer cutsheets (if buyer is a different user)
  const { data: buyerData, loading: buyerLoading } = useCutsheetSearchQuery({
    variables: {
      ...defaultQueryVars,
      buyerId: contactUserId,
    },
    skip: !open || !vendorId || !showBuyerTab,
    fetchPolicy: 'cache-and-network',
  });

  // Query 4: Producer cutsheets (when buyer is viewing their own split)
  const { data: producerData, loading: producerLoading } =
    useCutsheetSearchQuery({
      variables: {
        ...defaultQueryVars,
        type: [EnumCutsheetType.User],
        userId: producerId,
      },
      skip: !open || !vendorId || !showProducerTab,
      fetchPolicy: 'cache-and-network',
    });

  const houseCutsheets = useMemo(
    () => (houseData?.cutsheetSearch?.items ?? []) as Cutsheet[],
    [houseData],
  );
  const myCutsheets = useMemo(
    () => (myData?.cutsheetSearch?.items ?? []) as Cutsheet[],
    [myData],
  );
  const buyerCutsheets = useMemo(
    () => (buyerData?.cutsheetSearch?.items ?? []) as Cutsheet[],
    [buyerData],
  );
  const producerCutsheets = useMemo(
    () => (producerData?.cutsheetSearch?.items ?? []) as Cutsheet[],
    [producerData],
  );

  const shopName = job.vendor?.shop_name || 'House';

  const tabs = useMemo(() => {
    const t = [
      {
        label: 'Mine',
        cutsheets: myCutsheets,
        loading: myLoading,
      },
      {
        label: shopName,
        cutsheets: houseCutsheets,
        loading: houseLoading,
      },
    ];
    if (showProducerTab) {
      t.push({
        label: "Producer's",
        cutsheets: producerCutsheets,
        loading: producerLoading,
      });
    }
    if (showBuyerTab) {
      t.push({
        label: 'Buyer',
        cutsheets: buyerCutsheets,
        loading: buyerLoading,
      });
    }
    return t;
  }, [
    shopName,
    houseCutsheets,
    myCutsheets,
    producerCutsheets,
    buyerCutsheets,
    houseLoading,
    myLoading,
    producerLoading,
    buyerLoading,
    showProducerTab,
    showBuyerTab,
  ]);

  const refetchJob = useMemo(
    () => ({
      query: ProducerJobByIdDocument,
      variables: { id: job._id },
    }),
    [job._id],
  );

  const [updateCutsheets, { loading: saving }] =
    useProcessorSchedulingCutsheetsPartialUpdateMutation();

  const handleSelect = useCallback(
    async (cutsheet: Cutsheet) => {
      if (isLocked || saving) return;
      await updateCutsheets({
        variables: {
          processorSchedulingId: job._id,
          record: [{ _id: cutsheetInfo._id, cutsheet: cutsheet._id }],
        },
        // When a parent-supplied callback handles refetch, skip the default
        // ProducerJobById refetch to avoid unnecessary network requests.
        refetchQueries: onCutsheetAssigned ? [] : [refetchJob],
      });
      await onCutsheetAssigned?.();
      onClose();
    },
    [
      isLocked,
      saving,
      updateCutsheets,
      job._id,
      cutsheetInfo._id,
      onCutsheetAssigned,
      refetchJob,
      onClose,
    ],
  );

  const activeTabData = tabs[activeTab] ?? tabs[0];

  let drawerTitle = 'Select Cutsheet';
  if (isLocked) drawerTitle = 'Cutsheet (Locked)';
  else if (currentCutsheetId) drawerTitle = 'Change Cutsheet';

  return (
    <SplitSlotDrawer open={open} title={drawerTitle} onClose={onClose}>
      <Stack sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {isLocked && (
          <Alert
            severity="info"
            icon={<Lock fontSize="small" />}
            sx={{ mx: 2, mt: 2, flexShrink: 0 }}
          >
            This cutsheet has been locked by the processor and cannot be
            changed.
          </Alert>
        )}

        {cutsheetInfo.cutsheet && (
          <Box sx={{ px: 2, pt: 2, flexShrink: 0 }}>
            <CutsheetCard
              cutsheet={cutsheetInfo.cutsheet as Cutsheet}
              isSelected
              onSelect={() => {}}
              shopName={shopName}
            />
          </Box>
        )}

        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="fullWidth"
          sx={{ px: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
        >
          {map(tabs, (tab, i) => (
            <Tab
              key={i}
              label={tab.label}
              value={i}
              sx={{
                fontSize: '0.95rem',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                minHeight: 48,
              }}
            />
          ))}
        </Tabs>

        <Stack gap={1} sx={{ p: 2, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {activeTabData?.loading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                py: 4,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          )}

          {!activeTabData?.loading && activeTabData?.cutsheets.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 4, textAlign: 'center' }}
            >
              No cutsheets found.
            </Typography>
          )}

          {!activeTabData?.loading &&
            map(
              activeTabData?.cutsheets.filter(
                (cs) => cs._id !== currentCutsheetId,
              ),
              (cs) => (
                <CutsheetCard
                  key={cs._id}
                  cutsheet={cs}
                  isSelected={false}
                  onSelect={() => handleSelect(cs)}
                  shopName={shopName}
                />
              ),
            )}
        </Stack>
      </Stack>

      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          disabled={isLocked}
          onClick={onCreateNew}
          sx={{ textTransform: 'none', width: '100%', py: 1.5 }}
        >
          Create New Cutsheet
        </Button>
        {saving && (
          <Box sx={{ textAlign: 'center', mt: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="caption" sx={{ ml: 1 }}>
              Assigning cutsheet...
            </Typography>
          </Box>
        )}
      </Box>
    </SplitSlotDrawer>
  );
}
